import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	Avatar,
	Switch,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	Refresh,
	Stop,
	ExitToApp,
	SettingsBackupRestore,
	ErrorOutline,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { resolveContactProfilePicUrl } from "../../utils/resolveContactProfilePicUrl";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	profileContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
	},
	profileAvatar: {
		border: `2px solid ${green[500]}`,
		width: 40,
		height: 40,
	},
	profileAvatarOffline: {
		border: `2px solid #bdbdbd`,
		width: 40,
		height: 40,
		opacity: 0.85,
	},
	profileInfo: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
	},
	actionsRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexWrap: "nowrap",
	},
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const Connections = () => {
	const classes = useStyles();

	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [busyAction, setBusyAction] = useState("");

	const selectedLive = whatsApps?.find(wa => wa.id === selectedWhatsApp?.id) || selectedWhatsApp;

	useEffect(() => {
		if (qrModalOpen && selectedLive?.status === "CONNECTED") {
			setQrModalOpen(false);
			setSelectedWhatsApp(null);
		}
	}, [qrModalOpen, selectedLive]);

	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback((created) => {
		setWhatsAppModalOpen(false);
		if (created && created.id) {
			setSelectedWhatsApp(created);
			setQrModalOpen(true);
		} else {
			setSelectedWhatsApp(null);
		}
	}, []);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, []);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const runSessionAction = async (whatsAppId, path, successMessage) => {
		const key = `${path}-${whatsAppId}`;
		setBusyAction(key);
		try {
			const { data } = await api.post(`/whatsapp/${whatsAppId}/${path}`);
			if (successMessage) toast.success(successMessage);
			if (path === "reconfigure") {
				if (data?.changed) {
					toast.success(i18n.t("connections.toasts.reconfigured"));
				} else {
					toast.info(i18n.t("connections.toasts.reconfigureOk"));
				}
			}
		} catch (err) {
			toastError(err);
		} finally {
			setBusyAction("");
		}
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect" || action === "logout") {
			setConfirmModalInfo({
				action: "logout",
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId,
			});
		}

		if (action === "stop") {
			setConfirmModalInfo({
				action: "stop",
				title: i18n.t("connections.confirmationModal.stopTitle"),
				message: i18n.t("connections.confirmationModal.stopMessage"),
				whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: "delete",
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "logout") {
			try {
				await api.post(`/whatsapp/${confirmModalInfo.whatsAppId}/logout`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "stop") {
			try {
				await api.post(`/whatsapp/${confirmModalInfo.whatsAppId}/stop`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		const canLogin =
			whatsApp.status === "qrcode" ||
			whatsApp.status === "DISCONNECTED" ||
			whatsApp.status === "ERROR" ||
			whatsApp.status === "OPENING" ||
			whatsApp.status === "PAIRING" ||
			whatsApp.status === "TIMEOUT";

		return (
			<>
				{canLogin && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.login")}
					</Button>
				)}
				{whatsApp.status === "OPENING" && !canLogin && (
					<Button size="small" variant="outlined" disabled color="default">
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}
			</>
		);
	};

	const renderStatusToolTips = whatsApp => {
		return (
			<div className={classes.customTableCell}>
				{whatsApp.status === "DISCONNECTED" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "ERROR" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.error.title")}
						content={i18n.t("connections.toolTips.error.content")}
					>
						<ErrorOutline color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedLive?.id}
				qrcode={selectedLive?.qrcode}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenWhatsAppModal}
					>
						{i18n.t("connections.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								Perfil
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.session")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.default")}
							</TableCell>
							<TableCell align="center">
								Chatbot
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : (
							<>
								{whatsApps?.length > 0 &&
									whatsApps.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">
												{whatsApp.name}
											</TableCell>
											<TableCell align="center">
												{whatsApp.profile && (whatsApp.profile.name || whatsApp.profile.number || whatsApp.profile.picture) ? (
													<div className={classes.profileContainer}>
														{whatsApp.profile.picture && (
															<Avatar
																src={resolveContactProfilePicUrl(whatsApp.profile.picture)}
																alt={whatsApp.profile.name || whatsApp.profile.number}
																className={
																	whatsApp.status === "CONNECTED"
																		? classes.profileAvatar
																		: classes.profileAvatarOffline
																}
															/>
														)}
														<div className={classes.profileInfo}>
															<Typography variant="body2">
																{whatsApp.profile.name || whatsApp.profile.number || "-"}
															</Typography>
															{whatsApp.profile.name && whatsApp.profile.number && (
																<Typography variant="caption" color="textSecondary">
																	{whatsApp.profile.number}
																</Typography>
															)}
														</div>
													</div>
												) : (
													"-"
												)}
											</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{renderActionButtons(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											<TableCell align="center">
												{whatsApp.isDefault && (
													<div className={classes.customTableCell}>
														<CheckCircle style={{ color: green[500] }} />
													</div>
												)}
											</TableCell>
											<TableCell align="center">
												<Switch
													checked={whatsApp.enableChatbot || false}
													onChange={async (e) => {
														const newValue = e.target.checked;
														try {
															await api.put(`/whatsapp/${whatsApp.id}`, {
																name: whatsApp.name,
																greetingMessage: whatsApp.greetingMessage,
																farewellMessage: whatsApp.farewellMessage,
																isDefault: whatsApp.isDefault,
																enableChatbot: newValue,
																queueIds: whatsApp.queues?.map(q => q.id) || []
															});
															toast.success("Chatbot " + (newValue ? "habilitado" : "deshabilitado"));
														} catch (err) {
															toastError(err);
														}
													}}
													color="primary"
													size="small"
												/>
											</TableCell>
											<TableCell align="center">
												<div className={classes.actionsRow}>
													<Tooltip title={i18n.t("connections.buttons.restart")}>
														<IconButton
															size="small"
															disabled={busyAction === `restart-${whatsApp.id}`}
															onClick={() => runSessionAction(whatsApp.id, "restart")}
														>
															<Refresh />
														</IconButton>
													</Tooltip>
													<Tooltip title={i18n.t("connections.buttons.stop")}>
														<IconButton
															size="small"
															disabled={busyAction === `stop-${whatsApp.id}`}
															onClick={() => handleOpenConfirmationModal("stop", whatsApp.id)}
														>
															<Stop />
														</IconButton>
													</Tooltip>
													<Tooltip title={i18n.t("connections.buttons.logout")}>
														<IconButton
															size="small"
															onClick={() => handleOpenConfirmationModal("logout", whatsApp.id)}
														>
															<ExitToApp />
														</IconButton>
													</Tooltip>
													<Tooltip title={i18n.t("connections.buttons.reconfigure")}>
														<IconButton
															size="small"
															disabled={busyAction === `reconfigure-${whatsApp.id}`}
															onClick={() => runSessionAction(whatsApp.id, "reconfigure")}
														>
															<SettingsBackupRestore />
														</IconButton>
													</Tooltip>
													<Tooltip title={i18n.t("connections.buttons.edit")}>
														<IconButton
															size="small"
															onClick={() => handleEditWhatsApp(whatsApp)}
														>
															<Edit />
														</IconButton>
													</Tooltip>
													<Tooltip title={i18n.t("connections.buttons.delete")}>
														<IconButton
															size="small"
															onClick={() => handleOpenConfirmationModal("delete", whatsApp.id)}
														>
															<DeleteOutline />
														</IconButton>
													</Tooltip>
												</div>
											</TableCell>
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Connections;
